import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * BreadcrumbContext — lets any page inside DashboardLayout push dynamic segments
 * to the global breadcrumb trail (rendered by BackBar).
 *
 * Usage in a page component (e.g., a school detail view):
 *   useBreadcrumbExtra('ABC Public School');
 *   // → trail becomes: Home › Schools › ABC Public School
 *
 * Multiple segments:
 *   useBreadcrumbExtra([{ label: 'ABC Public School', path: '/schools/abc' }, { label: 'Students' }]);
 *
 * Replace the auto-generated current-page label entirely:
 *   useBreadcrumbExtra({ label: school.name }, { replace: true });
 */
const BreadcrumbContext = createContext(null);

export function BreadcrumbProvider({ children }) {
  // Each entry in the stack: { id, extras: Array<{label, path?}>, replace: boolean }
  const [stack, setStack] = useState([]);
  const location = useLocation();

  // Reset whole stack on route change so stale extras from a previous page never leak.
  useEffect(() => {
    setStack([]);
  }, [location.pathname]);

  const push = useCallback((id, extras, replace) => {
    setStack((prev) => {
      const without = prev.filter((e) => e.id !== id);
      return [...without, { id, extras, replace: !!replace }];
    });
  }, []);

  const remove = useCallback((id) => {
    setStack((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const value = useMemo(() => {
    // Resolve the active entry: the most recently pushed one. Pages typically
    // push only their own (one entry per route), but if a deeply-nested
    // component also pushes, the last push wins for "current page" framing.
    const active = stack[stack.length - 1] || null;
    return {
      extras: active?.extras || [],
      replaceCurrent: !!active?.replace,
      push,
      remove,
    };
  }, [stack, push, remove]);

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbContext() {
  // Returns null when used outside BreadcrumbProvider (e.g., public pages without DashboardLayout).
  return useContext(BreadcrumbContext);
}

/**
 * Hook for pages to add dynamic segment(s) to the breadcrumb trail.
 * Auto-cleans on unmount.
 *
 * @param input  string | {label, path?} | Array<string | {label, path?}>
 * @param options { replace?: boolean }  — when true, the dynamic segment(s) replace
 *                                          the current page's auto-generated label
 *                                          rather than appending to it.
 */
export function useBreadcrumbExtra(input, options = {}) {
  const ctx = useBreadcrumbContext();
  const { replace = false } = options;

  // Normalize input into Array<{label, path?}> deterministically per render.
  const normalized = useMemo(() => {
    if (input === null || input === undefined || input === '') return null;
    const arr = Array.isArray(input) ? input : [input];
    const out = arr
      .map((item) => {
        if (item === null || item === undefined || item === '') return null;
        if (typeof item === 'string') return { label: item };
        if (typeof item === 'object' && item.label) return { label: item.label, path: item.path };
        return null;
      })
      .filter(Boolean);
    return out.length ? out : null;
  }, [input]);

  // Stable per-component id: a single ref-like primitive (use Math.random for a unique id once).
  const id = useMemo(() => `br-${Math.random().toString(36).slice(2)}`, []);

  // push/remove are stable (useCallback []), so depending on them — instead of the
  // whole ctx object (which changes on every stack update) — avoids an infinite
  // render loop that would otherwise freeze navigation.
  const push = ctx?.push;
  const remove = ctx?.remove;
  useEffect(() => {
    if (!push || !remove) return undefined;
    if (normalized) push(id, normalized, replace);
    else remove(id);
    return () => remove(id);
  }, [id, normalized, replace, push, remove]);
}
